// H1.1 - Routing SSOT client (WAVY_AUDIT.md H1 stage 1).
//
// This module no longer imports hierarchy.js. Recipient options are resolved
// by the backend from the authoritative OrganizationNode hierarchy through
// GET /api/users/routing/approval-recipients. The frontend only DISPLAYS these
// options; the routing authority is server-side and every submission is
// re-validated there (POST /api/users/routing/validate-recipient and the
// uploadRoutes review handler).

const API_BASE = process.env.REACT_APP_API_URL || "";

const authHeaders = () => {
  // App-wide JWT key is "authToken" (Login.js); admins use "adminToken".
  // "token" was never written by any login flow — kept only as a legacy fallback.
  const token =
    localStorage.getItem("authToken") ||
    sessionStorage.getItem("authToken") ||
    localStorage.getItem("adminToken") ||
    localStorage.getItem("token") ||
    "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// In-memory cache so a dashboard session does not refetch per keystroke.
const cache = new Map();
const CACHE_TTL = 60 * 1000;

// H1.2 caches: clerk-network and SOI recipients are resolved server-side
// (OrganizationNode APPOINTMENT nodes) — no static frontend map any more.
const clerkCache = new Map();
let soiCache = { at: 0, recipients: [] };

const cacheKey = (branch, subBranch, unitBranch) =>
  `${(branch || "").toUpperCase()}|${(subBranch || "").toUpperCase()}|${(unitBranch || "").toUpperCase()}`;

/**
 * Resolve approval recipients for a destination scope from the authoritative
 * backend routing service. Resolves with an array of recipient names, or an
 * empty array when the routing structure is unavailable (fail-closed display).
 */
export async function fetchApprovalRecipients(branch, subBranch, unitBranch, options = {}) {
  const key = cacheKey(branch, subBranch, unitBranch);
  const cached = cache.get(key);
  if (!options.force && cached && Date.now() - cached.at < CACHE_TTL) return cached.recipients;

  const params = new URLSearchParams();
  if (branch) params.set("branch", branch);
  if (subBranch) params.set("subBranch", subBranch);
  if (unitBranch) params.set("unitBranch", unitBranch);

  try {
    const res = await fetch(
      `${API_BASE}/api/users/routing/approval-recipients?${params.toString()}`,
      { headers: { ...authHeaders() }, cache: options.force ? "no-store" : "default" }
    );
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || "Could not load recipients. Please retry.");
    }
    const data = await res.json();
    const recipients = Array.isArray(data.recipients) ? data.recipients : [];
    cache.set(key, { at: Date.now(), recipients });
    return recipients;
  } catch (error) {
    cache.delete(key);
    if (options.throwOnError) throw error;
    return [];
  }
}

/**
 * Ask the backend whether a recipient is permitted for a destination.
 * Pre-validation only; the backend re-validates on submit regardless.
 */
export async function validateRecipient(branch, subBranch, role, unitBranch) {
  try {
    const res = await fetch(`${API_BASE}/api/users/routing/validate-recipient`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ branch, subBranch, unitBranch, role }),
    });
    if (!res.ok) return { permitted: false, reason: "request_failed" };
    const data = await res.json();
    return { permitted: Boolean(data.permitted), reason: data.reason || null };
  } catch {
    return { permitted: false, reason: "network_error" };
  }
}

/**
 * Synchronous legacy-shaped accessor preserved for pages that call
 * getApprovalRecipients(branch, subBranch) directly. Returns the last cached
 * recipients for the scope (empty until fetchApprovalRecipients resolves).
 * New code should use fetchApprovalRecipients().
 */
export function getApprovalRecipients(branch, subBranch, unitBranch) {
  const cached = cache.get(cacheKey(branch, subBranch, unitBranch));
  return cached ? [...cached.recipients] : [];
}

/**
 * Global service-wide desks, identical to the backend GLOBAL_OTHERS list.
 * Display-only passthrough; the backend re-validates on submission.
 */
export const GLOBAL_OTHER_RECIPIENTS = ["MA", "DAC", "COMD"];

export function getAllRoles() {
  // Legacy export retained; the authoritative role list is the DB routing
  // service. Returns an empty array - callers must migrate to
  // fetchApprovalRecipients().
  return [];
}

// ===================== H1.2: CLERK NETWORK & SOI (DB-backed) =====================

/**
 * Fetch clerk-network recipients for a destination scope from the backend
 * (GET /api/users/routing/clerk-recipients). Replaces the static
 * CLERK_NETWORK_BY_UNIT map: the desks come from active APPOINTMENT nodes in
 * the OrganizationNode hierarchy. Resolves [] when the structure is
 * unavailable (fail-closed display).
 */
export async function fetchClerkRecipients(branch, subBranch, unitBranch) {
  const key = cacheKey(branch, subBranch, unitBranch);
  const cached = clerkCache.get(key);
  if (cached && Date.now() - cached.at < CACHE_TTL) return cached.recipients;

  const params = new URLSearchParams();
  if (branch) params.set("branch", branch);
  if (subBranch) params.set("subBranch", subBranch);
  if (unitBranch) params.set("unitBranch", unitBranch);

  try {
    const res = await fetch(
      `${API_BASE}/api/users/routing/clerk-recipients?${params.toString()}`,
      { headers: { ...authHeaders() } }
    );
    if (!res.ok) {
      clerkCache.set(key, { at: Date.now(), recipients: [] });
      return [];
    }
    const data = await res.json();
    const recipients = Array.isArray(data.recipients) ? data.recipients : [];
    clerkCache.set(key, { at: Date.now(), recipients });
    return recipients;
  } catch {
    clerkCache.set(key, { at: Date.now(), recipients: [] });
    return [];
  }
}

/**
 * Synchronous accessor for the last clerk-recipients cache of a scope.
 * Empty until fetchClerkRecipients resolves.
 */
export function getClerkRecipients(branch, subBranch, unitBranch) {
  const cached = clerkCache.get(cacheKey(branch, subBranch, unitBranch));
  return cached ? [...cached.recipients] : [];
}

/**
 * Fetch the SOI recipient roster from the backend
 * (GET /api/users/routing/soi-recipients). Replaces the static SOI_RECIPIENTS
 * / UNIT_RECIPIENTS.SOI map.
 */
export async function fetchSoiRecipients() {
  if (soiCache.at && Date.now() - soiCache.at < CACHE_TTL) return [...soiCache.recipients];
  try {
    const res = await fetch(`${API_BASE}/api/users/routing/soi-recipients`, {
      headers: { ...authHeaders() },
    });
    if (!res.ok) {
      soiCache = { at: Date.now(), recipients: [] };
      return [];
    }
    const data = await res.json();
    const recipients = Array.isArray(data.recipients) ? data.recipients : [];
    soiCache = { at: Date.now(), recipients };
    return recipients;
  } catch {
    soiCache = { at: Date.now(), recipients: [] };
    return [];
  }
}

/** Synchronous accessor for the last SOI recipients cache. */
export function getSoiRecipients() {
  return [...soiCache.recipients];
}

// ===================== END H1.2 =====================

// No per-name or per-session cache: a reassignment must invalidate the roster.
export async function fetchCurrentOfficeRecipients() {
  const response = await fetch(`${API_BASE}/api/users/routing/current-office-recipients`, {
    headers: authHeaders(), cache: "no-store",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || "Could not load your office recipients. Please retry.");
  if (!data.officeId || !Array.isArray(data.appointments) || !data.scope) {
    throw new Error("Invalid office roster. Please refresh recipients.");
  }
  return data;
}
